import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AR_TREND, arMap } from "@/i18n_halal";
import { PublicHeader } from "@/components/PublicHeader";
import { toast } from "sonner";
import { OwlLoader } from "@/components/OwlLoader";
import { OwlSpot } from "@/components/common";
import {
  ArrowLeft, Upload, Sparkles, TrendingUp, TrendingDown, Minus, Loader2, X, Eye,
  ShieldQuestion, ArrowUpRight, ArrowDownRight, ClipboardPaste,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TREND = {
  Uptrend: { Icon: TrendingUp, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  Downtrend: { Icon: TrendingDown, cls: "bg-rose-50 text-rose-700 border-rose-200" },
  Sideways: { Icon: Minus, cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

const Levels = ({ title, rows, up, empty }) => (
  <div>
    <div className="flex items-center gap-1.5 text-xs font-semibold">
      {up ? <ArrowUpRight className="h-3.5 w-3.5 text-rose-600" />
          : <ArrowDownRight className="h-3.5 w-3.5 text-emerald-600" />}{title}
    </div>
    <div className="mt-2 space-y-1.5">
      {(rows || []).length === 0 && <p className="text-[11px] text-muted-foreground">{empty}</p>}
      {(rows || []).map((r, i) => (
        <div key={`${r.level}-${i}`} className="rounded-xl border border-border p-2.5">
          <div className="numeric text-sm font-semibold">{r.level}</div>
          <div className="text-[11px] text-muted-foreground leading-relaxed">{r.note}</div>
        </div>
      ))}
    </div>
  </div>
);

export default function ChartLens() {
  const navigate = useNavigate();
  const { t, lang } = useApp();
  const ar = lang === "ar";
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [symbol, setSymbol] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [remaining, setRemaining] = useState(null);

  const take = useCallback((f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type))
      return toast.error(t("cl_err_type"));
    if (f.size > 6 * 1024 * 1024) return toast.error(t("cl_err_size"));
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  }, [t]);

  useEffect(() => {
    const onPaste = (e) => {
      const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
      if (item) take(item.getAsFile());
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [take]);

  const run = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      if (symbol) body.append("symbol", symbol);
      if (note) body.append("note", note);
      body.append("lang", lang);
      const { data } = await axios.post(`${API}/chart-lens`, body, { withCredentials: true });
      setResult(data);
      setRemaining(data.remaining_today);
      toast.success(t("cl_read_ok"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chart Lens could not read that image");
    } finally {
      setBusy(false);
    }
  };

  const a = result?.analysis;
  const tone = a ? (TREND[a.trend] || TREND.Sideways) : null;

  return (
    <div className="min-h-screen bg-background" data-testid="chart-lens-page">
      <PublicHeader />
      <div className="navy-strip text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14">
          <div className="mb-7 flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}
              className="text-white/70 hover:text-white hover:bg-white/10" data-testid="lens-back">
              <ArrowLeft className="h-4 w-4 me-1.5" />{t("hl_back")}
            </Button>
          </div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[color:var(--tharwy-lime)]">
            <Sparkles className="h-3.5 w-3.5" />{t("cl_eyebrow")}
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="lens-title">
            {t("cl_h1a")}<br />
            <span className="text-white/70">{t("cl_h1b")}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/70 leading-relaxed">{t("cl_sub")}</p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14 grid lg:grid-cols-2 gap-5">
        {/* upload */}
        <Card className="p-5 sm:p-6 shadow-premium" data-testid="lens-upload-card">
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); take(e.dataTransfer.files?.[0]); }}
            className="rounded-2xl border-2 border-dashed border-border hover:border-[#3E2E65] transition-colors cursor-pointer p-8 text-center"
            data-testid="lens-dropzone">
            {preview ? (
              <img src={preview} alt="chart to analyse" className="mx-auto max-h-64 rounded-xl" data-testid="lens-preview" />
            ) : (
              <>
                <Upload className="h-7 w-7 mx-auto text-muted-foreground" />
                <p className="mt-3 text-sm font-semibold">{t("cl_drop")}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  <ClipboardPaste className="inline h-3.5 w-3.5 me-1" />{t("cl_drop_hint")}
                </p>
              </>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            onChange={(e) => take(e.target.files?.[0])} data-testid="lens-file-input" />

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <Input placeholder={t("cl_ticker")} value={symbol} onChange={(e) => setSymbol(e.target.value)}
              data-testid="lens-symbol" />
            <Input placeholder={t("cl_note")} value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="lens-note" />
          </div>

          <div className="mt-4 flex items-center gap-3">
            <Button onClick={run} disabled={!file || busy} className="rounded-full px-6" data-testid="lens-run">
              {busy ? <Loader2 className="h-4 w-4 me-1.5 animate-spin" /> : <Eye className="h-4 w-4 me-1.5" />}
              {busy ? t("cl_running") : t("cl_run")}
            </Button>
            {file && (
              <Button variant="ghost" size="sm" onClick={() => { setFile(null); setPreview(null); setResult(null); }}
                data-testid="lens-clear"><X className="h-4 w-4 me-1" />{t("cl_clear")}</Button>
            )}
            {remaining !== null && (
              <span className="text-[11px] text-muted-foreground" data-testid="lens-remaining">
                {remaining} {t("cl_reads_left")}
              </span>
            )}
          </div>
          <p className="mt-4 text-[11px] text-muted-foreground leading-relaxed">{t("cl_quota_note")}</p>
        </Card>

        {/* result */}
        <div className="space-y-4">
          {!a && !busy && (
            <Card className="p-8 shadow-premium text-center" data-testid="lens-empty">
              <div className="flex justify-center"><OwlSpot /></div>
              <p className="mt-3 text-sm font-semibold">{t("cl_empty_title")}</p>
              <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto leading-relaxed">{t("cl_empty_sub")}</p>
            </Card>
          )}
          {busy && (
            <Card className="p-8 shadow-premium text-center" data-testid="lens-loading">
              <OwlLoader size="lg" className="justify-center" />
              <p className="mt-3 text-sm font-semibold">{t("cl_loading")}</p>
            </Card>
          )}
          {a && (
            <>
              <Card className="p-5 sm:p-6 shadow-premium" data-testid="lens-result">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${tone.cls}`}
                    data-testid="lens-trend">
                    <tone.Icon className="h-3.5 w-3.5" />{ar ? arMap(AR_TREND, a.trend) : a.trend} · {a.trend_strength}
                  </span>
                  {a.instrument && <Badge variant="outline" className="text-[10px]">{a.instrument}</Badge>}
                  {a.timeframe && <Badge variant="outline" className="text-[10px]">{a.timeframe}</Badge>}
                  <span className="ms-auto text-[11px] text-muted-foreground numeric">
                    {t("cl_confidence")} {a.confidence}%
                  </span>
                </div>
                <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-[#3E2E65]" style={{ width: `${a.confidence || 0}%` }} />
                </div>
                <p className="mt-4 text-[15px] leading-[1.7] text-foreground/85" data-testid="lens-summary">{a.summary}</p>

                <div className="mt-5 grid sm:grid-cols-2 gap-5">
                  <Levels title={t("cl_support")} rows={a.support} empty={t("cl_none_legible")} />
                  <Levels title={t("cl_resistance")} rows={a.resistance} up empty={t("cl_none_legible")} />
                </div>

                {(a.patterns || []).length > 0 && (
                  <div className="mt-5" data-testid="lens-patterns">
                    <div className="text-xs font-semibold">{t("cl_patterns")}</div>
                    <div className="mt-2 space-y-1.5">
                      {a.patterns.map((p, i) => (
                        <div key={`${p.name}-${i}`} className="text-xs">
                          <span className="font-semibold">{p.name}</span>
                          <span className="text-muted-foreground"> — {p.meaning}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-5 grid sm:grid-cols-2 gap-4 text-xs">
                  {a.momentum && (
                    <div><div className="font-semibold">{t("cl_momentum")}</div>
                      <p className="mt-1 text-muted-foreground leading-relaxed">{a.momentum}</p></div>
                  )}
                  {a.volume && (
                    <div><div className="font-semibold">{t("cl_volume")}</div>
                      <p className="mt-1 text-muted-foreground leading-relaxed">{a.volume}</p></div>
                  )}
                </div>

                {(a.watch_for || []).length > 0 && (
                  <div className="mt-5 rounded-2xl bg-secondary p-4" data-testid="lens-watch">
                    <div className="text-xs font-semibold">{t("cl_watch")}</div>
                    <ul className="mt-2 space-y-1.5">
                      {a.watch_for.map((w, i) => (
                        <li key={`${w}-${i}`} className="flex gap-2 text-xs text-muted-foreground">
                          <span className="mt-1.5 h-1 w-1 rounded-full bg-[#3E2E65] shrink-0" />{w}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(a.risks || []).length > 0 && (
                  <ul className="mt-4 space-y-1" data-testid="lens-risks">
                    {a.risks.map((r, i) => (
                      <li key={`${r}-${i}`} className="text-[11px] text-amber-700">· {r}</li>
                    ))}
                  </ul>
                )}
                {(a.unreadable || []).length > 0 && (
                  <p className="mt-3 text-[11px] text-muted-foreground">
                    {t("cl_unreadable")}: {a.unreadable.join("; ")}.
                  </p>
                )}
                <p className="mt-4 text-[11px] text-muted-foreground leading-relaxed border-t border-border pt-3">
                  {result.disclaimer}
                </p>
              </Card>

              {symbol && (
                <Button variant="outline" className="w-full rounded-full"
                  onClick={() => navigate(`/halal-stocks/${encodeURIComponent(symbol.toUpperCase())}`)}
                  data-testid="lens-screen-cta">
                  {t("cl_is_halal_a")} {symbol.toUpperCase()} {t("cl_is_halal_b")}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
